import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import moment, { type MomentInput } from "moment";
import { PhoneNumberUtil } from "google-libphonenumber";
import { AxiosError } from "axios";
import { toaster } from "@/components/Toaster";

export const _token = '_token';
export const _resident = '_resident';
export const _user = '_user';
export const _config = '_config';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export type ObjectType = Record<string, any>;


/**
 * Function to validate phone number using libphonenumber
 * @param phoneNumber
 * @param defaultCountry
 * @returns
 */
export const isValidPhoneNumber = (
	phoneNumber: string,
	defaultCountry: string = "GH"
) => {
	const phoneUtil = PhoneNumberUtil.getInstance();

	if (!phoneNumber) return false;

	try {
		const num = phoneUtil.parseAndKeepRawInput(phoneNumber, defaultCountry);
		return phoneUtil.isValidNumber(num);
	} catch (error) {
		console.error(error);
		return false;
	}
};

export default function formatMoney(
	currency: string = "GHS",
	amount: string = "0"
) {
	const options: Intl.NumberFormatOptions = {
		style: "currency",
		currency: currency,
		minimumFractionDigits: 2,
	};

	// check if its a clean amount
	if (parseInt(amount) % 2 === 0) {
		options.minimumFractionDigits = 0;
	}

	const formatter = Intl.NumberFormat("en-US", options);
	const formatted_amount = formatter.format(
		parseInt(
			(amount.length >= 5 && amount.length <= 6
				? parseInt(amount) / 1000
				: amount) as string
		)
	);

	return amount.length >= 5 && amount.length <= 6
		? formatted_amount + "K"
		: formatted_amount;
}

/**
 * Format Date with moment FOR INPUT
 * @param {string} date
 * @param {string} format
 */
export const formatDate = (date: MomentInput, format = "YYYY-MM-DD") => {
	if (!date) {
		return "—";
	}

	return moment(date).format(format);
}

/**
 * Generate a fully qualified url
 * @param {string} path
 * @return string
 */
export const withPrefix = (path: string) => {
	const postfix = import.meta.env.VITE_HOST_POSTFIX || "";
	const baseURL = window.location.origin + postfix;
	return baseURL + path;
};

/**
 * Generate a fully qualified url
 * @param {string} path
 * @return string
 */
export const withFilePrefix = (path: string) => {
	const postfix = import.meta.env.VITE_FILE_POSTFIX || "";
	const baseURL = window.location.origin + postfix;
	return baseURL + path;
};

export const maxSizeInMB = 40;

export const isFileSizeOk = (size: number) => {
	const maxSizeInBytes = maxSizeInMB * 1024 * 1024; // Convert MB to Bytes
	return size <= maxSizeInBytes;
};

export function handleMutationError(error: AxiosError<{ message?: string; error?: string, errors?: Record<string, string[]> }>) {
	// const message = error?.response?.data?.message ?? error?.message ?? "Something went wrong";
	const message = error?.response?.data?.errors ? Object.values(error?.response?.data?.errors).flat().join(", ") :
		error?.response?.data?.error
		?? error?.response?.data?.message
		?? error?.message
		?? "Something went wrong";
	toaster("error", message);
}

export function formFileDownloadByPathUrl(filePath: string, cacheKey?: string): string {
	const url = withFilePrefix(`api/v1/media/download-by-path/${filePath}`);

	if (!cacheKey) {
		return url;
	}

	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}v=${encodeURIComponent(cacheKey)}`;
}

export function formFileDownloadMediaUrl(filePath: string): string {
	return withFilePrefix(`api/v1/media/file-media/${filePath}`);
}
